import { useRouter } from 'next/router';
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useSyncExternalStore,
} from 'react';

/**
 * Writing direction of the signed-in app. In an RTL locale (Arabic) every page
 * that renders the app shell (DashboardLayout / NarrowDashboardLayout, through
 * `useDashboardChromePresence`) gets `<html dir="rtl">`: on the server through
 * _document, on the client for as long as the shell is mounted. It goes on
 * <html>, not on the layout's div, so portals (dialogs, menus, toasts) mirror
 * too and code can read `document.documentElement.dir`. Marketing pages are
 * left alone: the ones built for RTL set `dir` on their own root.
 */
export type TextDirection = 'ltr' | 'rtl';

const RTL_LANGUAGES = new Set(['ar']);

export function localeDirection(locale: string | undefined): TextDirection {
  return RTL_LANGUAGES.has((locale || '').split('-')[0]) ? 'rtl' : 'ltr';
}

/**
 * Server render only. _document hands _app an empty object (the
 * `appShellDirection` prop), the shell writes its direction into it while the
 * page renders, and _document then sets `<html dir>` from it.
 */
export type AppShellDirection = { dir?: TextDirection };

export const AppShellDirectionContext = createContext<AppShellDirection | null>(
  null,
);

// Shells that asked for rtl. A count, so the old page's cleanup and the new
// page's effect can run in any order during a client navigation.
let rtlShells = 0;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = (): TextDirection => (rtlShells > 0 ? 'rtl' : 'ltr');
const getServerSnapshot = (): TextDirection => 'ltr';

const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

/** Called by the app shell (see `useDashboardChromePresence`). */
export function useAppShellDirection(): void {
  const { locale } = useRouter();
  const dir = localeDirection(locale);

  const serverRender = useContext(AppShellDirectionContext);
  if (serverRender) serverRender.dir = dir;

  // A layout effect, so the page never paints in the old direction and
  // children's effects already read the new `document.documentElement.dir`.
  useIsomorphicLayoutEffect(() => {
    if (dir !== 'rtl') return;
    rtlShells += 1;
    document.documentElement.dir = 'rtl';
    notify();
    return () => {
      rtlShells -= 1;
      if (rtlShells === 0) document.documentElement.removeAttribute('dir');
      notify();
    };
  }, [dir]);
}

/**
 * The current page's direction, for components that take it as a prop
 * (antd's ConfigProvider, Radix menus). 'ltr' on the server and during
 * hydration, then the shell's direction.
 */
export function useDocumentDirection(): TextDirection {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
